import { useEffect, useState } from "react";
import { api } from "../../api";
import { fmtInt, type Live } from "../../web-analytics";

const POLL_MS = 30_000;

/**
 * Visitors in the last five minutes, refreshed every 30 s while the tab is
 * visible (and at once when it comes back). A failed poll keeps the last count.
 * Ported from Sonar's LiveBadge.
 */
export default function LiveBadge({ clientId, siteId, filters, initial }: {
  clientId: number;
  siteId: number;
  /** Raw `dim:value` strings, as the page URL carries them. */
  filters: string[];
  /** The sites list's unfiltered count; used until the first poll returns. */
  initial: number | null;
}) {
  const [live, setLive] = useState<number | null>(initial);
  const q = new URLSearchParams({ site: String(siteId) });
  for (const f of filters) q.append("f", f);
  const path = `/api/web/${clientId}/live?${q}`;

  useEffect(() => {
    setLive(filters.length ? null : initial);
    const controller = new AbortController();
    let inFlight = false;
    const poll = async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      try {
        const r = await api<Live>(path, { signal: controller.signal });
        if (Number.isFinite(r.live) && r.live >= 0) setLive(r.live);
      } catch {
        // Keep the last known count.
      } finally {
        inFlight = false;
      }
    };
    void poll();
    const id = window.setInterval(poll, POLL_MS);
    document.addEventListener("visibilitychange", poll);
    return () => {
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", poll);
    };
    // `path` carries site and filters; `initial` only seeds the first paint.
  }, [path]);

  const active = (live ?? 0) > 0;
  return (
    <span className={active ? "wa-live active" : "wa-live"} title="Visitors in the last 5 minutes">
      <i className="wa-live-dot" aria-hidden="true" />
      <strong>{live === null ? "—" : fmtInt(live)}</strong> live
    </span>
  );
}
